import { createSignal, For, onCleanup, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { randomDeck } from "../../app/decks";
import { buildMode, setBuildMode, type BuildMode } from "../../app/buildMode";
import {
  buildCost,
  buildSize,
  DEFAULT_CONFIG,
  DRAFT_BUDGET,
  type BoardConfig,
  type DeckBuild,
  type Difficulty,
  type CardTypeId,
} from "../../core";
import { DeckBuilder } from "../components/DeckBuilder";
import { ThemeToggle } from "../components/ThemeToggle";
import { Icon } from "../components/Icon";

export interface AiStartConfig {
  name: string;
  /** How the deck is built. In "draft" mode `build` is chosen later, on the draft screen. */
  mode: BuildMode;
  build?: DeckBuild;
  config: BoardConfig;
  difficulty: Difficulty;
}

const BUDGET_OPTIONS = [20, 30, 40, 60];

const DIFFICULTIES: readonly { id: Difficulty; label: string; blurb: string }[] = [
  { id: "easy", label: "Easy", blurb: "Loose play — grabs what it sees, often slips up." },
  { id: "normal", label: "Normal", blurb: "Greedy and sensible — punishes hanging pieces." },
  { id: "hard", label: "Hard", blurb: "Looks a move ahead and guards its royals." },
];

export function AiSetupScreen(props: { onStart: (cfg: AiStartConfig) => void; onBack?: () => void }) {
  const [budget, setBudget] = createSignal(DEFAULT_CONFIG.budget);
  const [difficulty, setDifficulty] = createSignal<Difficulty>("normal");
  const [name, setName] = createSignal("You");
  const [build, setBuild] = createStore<DeckBuild>({});
  const mode = buildMode; // "draft" (default) | "classic"

  const config = (): BoardConfig => ({ ...DEFAULT_CONFIG, budget: budget() });

  const withinBudget = () => buildCost(build) <= budget();
  const hasPieces = () => buildSize(build) > 0;
  const canStart = () => withinBudget() && hasPieces();

  function change(type: CardTypeId, count: number) {
    setBuild(type, count);
  }

  function start() {
    window.clearInterval(timer);
    if (mode() === "draft") {
      props.onStart({
        name: name(),
        mode: "draft",
        config: { ...DEFAULT_CONFIG, budget: DRAFT_BUDGET },
        difficulty: difficulty(),
      });
      return;
    }
    if (!canStart()) return;
    props.onStart({
      name: name(),
      mode: "classic",
      build: { ...build },
      config: config(),
      difficulty: difficulty(),
    });
  }

  // Classic deck-building is on the clock — when it runs out the match starts
  // with your current deck, or a random one if it isn't legal yet. Draft mode
  // has no setup timer (the drafting itself is the activity).
  const DECK_SECONDS = 120;
  const [timeLeft, setTimeLeft] = createSignal(DECK_SECONDS);
  const timer = window.setInterval(() => {
    if (mode() === "draft") return; // no countdown while drafting is the plan
    setTimeLeft((t) => {
      if (t > 1) return t - 1;
      window.clearInterval(timer);
      if (canStart()) start();
      else
        props.onStart({
          name: name(),
          mode: "classic",
          build: randomDeck(budget()),
          config: config(),
          difficulty: difficulty(),
        });
      return 0;
    });
  }, 1000);
  onCleanup(() => window.clearInterval(timer));

  const clock = () => {
    const t = timeLeft();
    return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
  };

  return (
    <div class="app">
      <div class="topbar">
        <div class="brand">
          <h1>Chessdeck</h1>
          <span class="tag">chess, but you build the army</span>
        </div>
        <div class="topbar-actions">
          <Show when={props.onBack}>
            <button class="btn btn-ghost" onClick={() => props.onBack!()}>
              ← Home
            </button>
          </Show>
          <ThemeToggle />
        </div>
      </div>

      <div class="setup">
        <div class="panel">
          <h2>Match settings</h2>
          <div class="field-row">
            <div class="field">
              <label>How you build your deck</label>
              <div class="seg">
                <button classList={{ active: mode() === "draft" }} onClick={() => setBuildMode("draft")}>
                  Draft
                </button>
                <button classList={{ active: mode() === "classic" }} onClick={() => setBuildMode("classic")}>
                  Classic
                </button>
              </div>
            </div>
            <div class="field">
              <label>Computer difficulty</label>
              <div class="seg">
                <For each={DIFFICULTIES}>
                  {(d) => (
                    <button
                      classList={{ active: difficulty() === d.id }}
                      title={d.blurb}
                      onClick={() => setDifficulty(d.id)}
                    >
                      {d.label}
                    </button>
                  )}
                </For>
              </div>
            </div>
            <Show when={mode() === "classic"}>
              <div class="field">
                <label>Budget per player</label>
                <div class="seg">
                  <For each={BUDGET_OPTIONS}>
                    {(b) => (
                      <button classList={{ active: budget() === b }} onClick={() => setBudget(b)}>
                        {b}
                      </button>
                    )}
                  </For>
                </div>
              </div>
            </Show>
          </div>
          <div class="field" style={{ "margin-top": "10px" }}>
            <label>Your name</label>
            <input type="text" value={name()} onInput={(e) => setName(e.currentTarget.value)} />
          </div>
          <p class="budget-sub" style={{ "margin-top": "10px" }}>
            {DIFFICULTIES.find((d) => d.id === difficulty())!.blurb}
            {mode() === "draft"
              ? " You and the computer draft from a shared pool, taking turns — a gentle way to meet the pieces."
              : " The computer drafts its own deck to the same budget."}
          </p>
        </div>

        <Show
          when={mode() === "classic"}
          fallback={
            <div class="panel draft-intro">
              <h2>Draft your army</h2>
              <p class="budget-sub">
                You'll take turns picking cards from a shared pool with the computer, spending a{" "}
                {DRAFT_BUDGET}-point budget. Each card shows what it does, so you learn as you draft.
              </p>
              <div class="field-row" style={{ "justify-content": "flex-end", "margin-top": "12px" }}>
                <button class="btn btn-primary" onClick={start}>
                  Start draft
                </button>
              </div>
            </div>
          }
        >
          <div class="field-row" style={{ gap: "8px", "justify-content": "space-between" }}>
            <strong style={{ "font-size": "13px" }}>Build your deck</strong>
            <span class="deck-timer" classList={{ urgent: timeLeft() <= 15 }} title="Deck creation time — the match auto-starts at 0:00">
              <Icon name="clock" /> {clock()}
            </span>
          </div>

          <DeckBuilder
            owner={0}
            name={name()}
            build={build}
            config={config()}
            onName={(n) => setName(n)}
            onChange={change}
          />

          <div class="field-row" style={{ "justify-content": "flex-end" }}>
            <button class="btn btn-primary" disabled={!canStart()} onClick={start}>
              {canStart() ? "Play the computer" : "Add at least one piece"}
            </button>
          </div>
        </Show>
      </div>
    </div>
  );
}
